import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useState } from 'react';

import { aggErrors } from 'app-components/flux/account-discovery';
import { accountsUtils } from 'app-components/flux/accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { tracker, getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

const log = getLogger('BillPresentmentConnectBox');

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

import ExitIcon from '@mui/icons-material/Clear';
import LinkIcon from '@mui/icons-material/LinkRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// CUSTOM COMPONENTS
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import QButton from 'components/QButton';

const useStyles = makeStyles()((theme) => ({
  connectBox: {
    height: 76,
    width: 'calc(100% - 48px)',
    padding: '12px 28px 12px 20px',
    flexShrink: 0,
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '12px 24px 4px',
    position: 'relative',

    color: theme.palette.info.dark,
    backgroundColor: theme.palette.info.light,
  },
  errorBox: {
    height: 76,
    width: 'calc(100% - 48px)',
    padding: '12px 28px 12px 20px',
    flexShrink: 0,
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '12px 24px 4px',
    position: 'relative',

    // color: theme.palette.warning.dark,
    backgroundColor: theme.palette.error.light,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
  },
  dismiss: {
    position: 'absolute',
    borderRadius: 10,
    top: 6,
    right: 6,
    height: 20,
    width: 20,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  exitSize: {
    height: 16,
    width: 16,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
}));

interface BillPresentmentConnectBoxProps {
  model?: ScheduledTransaction;
  onConnect: () => void;
  location: string; // mixpanel tracking
  onEdit?: (tabIndex?: number) => void;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function BillPresentmentConnectBox(props: BillPresentmentConnectBoxProps) {
  const { classes } = useStyles();
  const { model, onConnect, location, onEdit } = props;
  const dispatch = useAppDispatch();

  const txn = model?.transaction;

  // BPS
  const connectOption = useAppSelector((state) => billPresentmentSelectors.getShowConnectOption(state, model));

  // BVA
  const creditAccount = useAppSelector((state) => txn && transactionsSelectors.getCreditAccountFromTxn(state, txn));
  const institutionLogin = useAppSelector((state) =>
    creditAccount?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, creditAccount.institutionLoginId)
      : undefined,
  );

  const canBVA = useAppSelector((state) => billPresentmentSelectors.getCanBVA(state, model));

  const { autoAdjustAmount, autoAdjustDueOn } = model || {};
  const hasBVA = Boolean(
    creditAccount &&
      transactionsUtils.isTransferTxn(txn) &&
      ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn),
  );

  const [dismissed, setDismissed] = useState(false);

  if (!model) {
    return null;
  }

  // ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
  // ### - - -                    BVA displays                     - - - ###
  // ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

  if (canBVA && creditAccount) {
    const reconnect = accountsUtils.isAccountInError(creditAccount)?.isError;
    const balanceText = scheduledTransactionsUtils.getDisplayNameFromAdjustAmount(autoAdjustAmount);
    return (
      <div className={reconnect ? classes.errorBox : classes.connectBox}>
        <div className={classes.stack}>
          <Typography variant='bodyMedium'>Bill Connect</Typography>
          <Typography variant='captionRegular'>
            {reconnect
              ? 'Linked account has been disconnected and reminder details can’t be updated.'
              : hasBVA
                ? `Reminder amount will be updated to the ${balanceText} once statement period closes.`
                : 'Automatically update your reminders with up-to-date bill details by connecting this biller.'}
          </Typography>
        </div>

        <QButton
          variant={hasBVA ? 'text' : 'contained'}
          onClick={() => {
            if (reconnect) {
              const aggregation = creditAccount?.aggregators?.get(0);
              const aggAction = aggErrors.getQcsAggregationAction(
                institutionLogin,
                aggregation?.aggStatus,
                aggregation?.aggStatusCode,
                store && featureFlagsSelectors.getFeatureFlags(store.getState()),
              );
              if (aggAction) {
                aggErrors.handleAggAction(dispatch, aggAction, 'BVA connection status');
              } else {
                log.error(
                  'No aggAction available for BillPresentmentConnectBox reconnect',
                  '\nCredit Account ID:',
                  creditAccount?.id,
                  '\nInstitution Login ID:',
                  institutionLogin?.id,
                  '\nInstitution:',
                  institutionLogin?.name,
                  '\nAgg Status:',
                  aggregation?.aggStatus,
                  '\nAgg Status Code:',
                  aggregation?.aggStatusCode,
                );
              }
              onConnect?.();
            } else {
              onEdit?.(2);
            }
          }}
          id={hasBVA ? 'BVA-edit' : 'BVA-connect'}
        >
          {!reconnect && hasBVA ? <LinkIcon style={{ marginRight: 4 }} /> : null}
          {reconnect ? 'Reconnect' : hasBVA ? 'Edit' : 'Connect'}
        </QButton>
      </div>
    );
  }

  // ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
  // ### - - -                    BPS Displays                     - - - ###
  // ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

  const reconnect = Boolean(
    model.presentmentAccountId &&
      (model.billerEnrollmentStatus === 'FAILED' || model.billerEnrollmentStatus === 'DISCONNECTED'),
  );

  const startConnect = () => {
    dispatch(doBillPresentment(model, location));
    tracker.track(reconnect ? tracker.events.fixBillerStart : tracker.events.addBillerStart, { initiator: location });
    onConnect?.();
  };

  const dismiss = () => {
    setDismissed(true);
  };

  return (connectOption || reconnect) && !dismissed ? (
    <div className={reconnect ? classes.errorBox : classes.connectBox}>
      <div className={classes.stack}>
        <Typography variant='bodyMedium'>{reconnect ? 'Unable to connect to your bill' : 'Connect biller'}</Typography>
        <Typography variant='captionRegular'>
          {reconnect
            ? "We're having trouble connecting to your biller. Try reconnecting."
            : "We'll keep Simplifi updated with your most recent bill information."}
        </Typography>
      </div>

      <QButton variant='contained' onClick={startConnect} id='series-connect'>
        <LinkIcon style={{ marginRight: 4 }} />
        {reconnect ? 'RECONNECT' : 'CONNECT'}
      </QButton>

      <ButtonBase id='dismiss_connect' className={classes.dismiss} onClick={dismiss} focusRipple>
        <ExitIcon className={classes.exitSize} />
      </ButtonBase>
    </div>
  ) : null;
}

BillPresentmentConnectBox.displayName = 'BillPresentmentConnectBox';

export default BillPresentmentConnectBox;
